<template>
  <svg
    version="1.1"
    viewBox="0 0 200 200"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
  >
    <circle
      cx="100"
      cy="100"
      :r="circleRay"
      class="circle-chart background"
      :transform="rotate"
      :fill="gradient"
      :stroke-width="hasScore ? 3 : 8"
      :stroke-dasharray="`${dashValue}, 1000`"
      :stroke="backgroundStroke"
    />

    <defs>
      <linearGradient
        :id="`${name}CircleChartGrad`"
        :x1="grad.x1"
        :x2="grad.x2"
        :y1="grad.y1"
        :y2="grad.y2"
      >
        <stop offset="0%" class="start" />
        <stop offset="100%" class="end" :style="stopColor" />
      </linearGradient>
    </defs>

    <circle
      cx="100"
      cy="100"
      :r="circleRay"
      class="circle-chart score"
      :transform="rotate"
      :stroke="color"
      :style="{ transition: circleTransition }"
      :stroke-dasharray="dasharrayScore"
    />

    <text
      class="text-score"
      text-anchor="middle"
      v-if="label"
      :stroke="backColor"
      :stroke-width="hasScore ? '0' : '0.5px'"
      :x="centerText"
      :y="scorePosition"
    >
      {{ scoreText || labelScore }}
      <tspan v-if="isPercentage">%</tspan>
    </text>

    <g v-if="score !== null" class="text-group">
      <slot :label="label" />
    </g>
  </svg>
</template>

<script>
import { mapGetters } from 'vuex'
import { getValueByScore } from '@/directives/metricColor.js'
import { isGradCompatible } from '@/helpers/userAgent'
import colors from '@/styles/custom/_colors.scss'
import debounce from '@/helpers/debounce'

const valueColors = {
  low: colors['metric-low'],
  medium: colors['metric-medium'],
  high: colors['metric-high'],
  blank: colors['metric-blank'],
}

const labels = resources => [
  { score: 4.5, label: resources.low },
  { score: 5.5, label: resources.okay },
  { score: 7.5, label: resources.good },
  { score: 9.0, label: resources.veryGood },
  { score: 10.1, label: resources.awesome },
]

export default {
  props: {
    name: { type: String, default: '' },
    grad: {
      type: Object,
      default: () => ({ x1: '10%', x2: '-70%', y1: '-10%', y2: '100%' }),
    },
    score: { type: Number, default: undefined },
    rotate: { type: String, default: 'rotate(140, 100, 100)' },
    dashValue: { type: Number, default: 340 },
    circleRay: { type: Number, default: 75 },
    scoreText: { type: Number, default: 0 },
    isPercentage: { type: Boolean, default: false },
    scorePosition: { type: Number, default: 115 },
  },

  data() {
    return {
      lastScore: undefined,
      backColor: colors['light'],
      dasharray: `${this.dashValue}, 1000`,
      centerText: 100,
      gradient: 'white',
    }
  },

  watch: {
    score(value) {
      this.processScore(value)
    },
  },

  computed: {
    ...mapGetters({ resources: 'circleChart' }),

    label() {
      return this.findLabel ? this.findLabel.label : ''
    },

    color() {
      return this.hasScore
        ? getValueByScore(this.score, valueColors)
        : colors['light']
    },

    hasScore() {
      return !isNaN(this.score)
    },

    findLabel() {
      return labels(this.resources).find(i => this.score < i.score)
    },

    stopColor() {
      return 'stop-color:' + (this.hasScore ? this.color : 'white')
    },

    canTransit() {
      return this.hasScore && this.backColor === colors['gray03']
    },

    labelScore() {
      return this.isPercentage ? (this.score * 10).toFixed(0) : this.score
    },

    dasharrayScore() {
      return this.hasScore ? this.dasharray : `${this.dashValue}, 1000`
    },

    backgroundStroke() {
      return this.hasScore ? this.backColor : colors['gray00']
    },

    circleTransition() {
      return this.canTransit ? 'stroke-dasharray ease-out 0.4s' : ''
    },
  },
  methods: {
    processScore(value) {
      this.hasScore ? this.showChart(value) : this.hideChart()
      this.lastScore = value
    },
    hideChart() {
      this.dasharray = `${this.dashValue}, 1000`
      this.backColor = colors['light']
    },
    showChart(value) {
      this.dasharray = this.lastScore
        ? `${(this.lastScore * this.dashValue) / 10}, 1000`
        : '0, 1000'
      debounce(50, () => {
        this.dasharray = `${(value * this.dashValue) / 10}, 1000`
        this.backColor = colors['gray03']
      })()
    },
    fillGradient(isCompatible) {
      this.gradient = isCompatible
        ? `url(#${this.name}CircleChartGrad)`
        : 'white'
    },
  },
  created() {
    this.fillGradient(isGradCompatible)
    this.processScore(this.score)
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

svg {
  max-width: 250px;
  min-width: 250px;
  min-height: 250px;
  margin-bottom: -30px;

  .circle-chart {
    stroke-linecap: round;

    &.score {
      stroke-width: 7px;
      fill: none;
    }
  }

  stop {
    &.start {
      stop-opacity: 0.5;
      stop-color: white;
    }

    &.end {
      stop-opacity: 0.1;
      transition: stop-color ease-out 0.4s;
    }
  }

  tspan {
    font-size: 15px;
  }

  .text-score {
    font-size: 55px;
    fill: black;
  }

  .text-group {
    transition: opacity 0.1s ease-out, transform 6s ease-out;
    text-anchor: middle;
    font-size: 13px;
    font-weight: 500;
    fill: map-get($theme-colors, 'gray03');
  }
}
</style>
